import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { createApi } from "@reduxjs/toolkit/query/react";
import { apiUrls } from "constants/apiUrls";
import { baseQueryWithReAuth } from "utils/baseQueryWithReAuth";
import { getResolvedUrl } from "utils/getResolvedUrl";
import {
    AgencyJobData,
    AgencyJobListingRes,
    ApplyJob,
    CreateJobCategoryProps,
    CreateUpdateJobParams,
    FollowUnFollowAgent,
    GetAgencyJobListingParams,
    GetJobByIdParams,
    GetJobPurposeResponse,
    GetJobsListing,
    JobCategory,
    JobLike,
    Jobs,
    JobsListingResponse,
    JobsParams,
    JobsResponse,
    NewJobData,
} from "./types";

export const initialState: Jobs = {
    jobs: [],
    newJobData: {
        isEdit: false,
    },
    selectedJobId: undefined,
};

export const jobSlice = createSlice({
    name: "jobs",
    initialState,
    reducers: {
        updateNewJobData: (state, { payload }: PayloadAction<NewJobData>) => {
            return {
                ...state,
                newJobData: {
                    ...payload,
                    job_details: payload.job_details,
                    method: payload.isEdit ?? false,
                },
            };
        },
        clearJobData: (state) => {
            return {
                jobs: [],
                newJobData: {
                    isEdit: false,
                },
            };
        },
        updateSelectedEditJob: (state, { payload }: PayloadAction<any>) => {
            return {
                ...state,
                selectedJobId: payload,
            };
        },
    },
});

export const jobsApiSlice = createApi({
    reducerPath: "jobsApi",
    baseQuery: baseQueryWithReAuth,
    endpoints(builder) {
        return {
            getJobs: builder.mutation<JobsResponse, JobsParams>({
                query: (params) => ({
                    url: apiUrls.usersWithOutUserId,
                    params,
                    method: "GET",
                }),
            }),
            getJobCategories: builder.query<JobCategory[], string>({
                query: (user_id) => ({
                    url: getResolvedUrl(apiUrls.agencyCategories, { user_id }),
                }),
            }),
            addJobCategory: builder.mutation<
                JobCategory,
                CreateJobCategoryProps
            >({
                query: ({ name, user_id }) => ({
                    url: getResolvedUrl(apiUrls.agencyCategories, { user_id }),
                    method: "POST",
                    body: {
                        name,
                    },
                }),
            }),
            getJobPurposes: builder.query<GetJobPurposeResponse, any>({
                query: () => ({
                    url: apiUrls.jobPurpose,
                }),
            }),
            createJob: builder.mutation<any, CreateUpdateJobParams>({
                query: ({ agency_id, data }) => ({
                    url: getResolvedUrl(apiUrls.createJob, { agency_id }),
                    method: "POST",
                    body: data,
                }),
            }),
            updateJob: builder.mutation<any, CreateUpdateJobParams>({
                query: ({ agency_id, data, job_id }) => ({
                    url: getResolvedUrl(apiUrls.updateJob, {
                        agency_id,
                        job_id: job_id ?? "",
                    }),
                    method: "PUT",
                    body: data,
                }),
            }),
            agencyJobListing: builder.query<
                AgencyJobListingRes,
                GetAgencyJobListingParams
            >({
                query: ({ agency_id, type }) => ({
                    url: `${
                        process.env.REACT_APP_API_URL
                    }/api/v1/agencies/${agency_id}/jobs${
                        type === "all" ? "" : `?status=${type}`
                    }`,
                }),
                keepUnusedDataFor: 0,
            }),
            getJobById: builder.query<AgencyJobData, GetJobByIdParams>({
                query: ({ agency_id, job_id }) => ({
                    url: getResolvedUrl(apiUrls.getJobById, {
                        agency_id,
                        job_id,
                    }),
                }),
                keepUnusedDataFor: 0,
            }),
            getJobsListing: builder.mutation<
                JobsListingResponse,
                GetJobsListing
            >({
                query: (params) => ({
                    url: apiUrls.getJobsListing,
                    params,
                    method: "GET",
                }),
            }),
            getLikedJobsListing: builder.query<
                JobsListingResponse,
                { user_id: string }
            >({
                query: ({ user_id }) => ({
                    url: getResolvedUrl(apiUrls.getLikeJob, {
                        user_id,
                    }),
                }),
            }),
            likeJob: builder.mutation<AgencyJobListingRes, JobLike>({
                query: (params) => ({
                    url: apiUrls.likeJob,
                    method: "POST",
                    body: params,
                }),
            }),
            applyJob: builder.mutation<AgencyJobListingRes | unknown, ApplyJob>(
                {
                    query: ({ job_id, user_id }) => ({
                        url: getResolvedUrl(apiUrls.applyJob, {
                            job_id,
                        }),
                        method: "POST",
                        body: { user_id },
                    }),
                }
            ),
            followAgent: builder.mutation<
                AgencyJobListingRes,
                FollowUnFollowAgent
            >({
                query: ({ agent_id, user_id }) => ({
                    url: getResolvedUrl(apiUrls.followAgent, {
                        agency_id: agent_id,
                        user_id,
                    }),
                    method: "POST",
                }),
            }),
            unFollowAgent: builder.mutation<
                AgencyJobListingRes,
                FollowUnFollowAgent
            >({
                query: ({ agent_id, user_id }) => ({
                    url: getResolvedUrl(apiUrls.unFollowAgent, {
                        agency_id: agent_id,
                        user_id,
                    }),
                    method: "DELETE",
                }),
            }),
        };
    },
});

export const {
    useGetJobsMutation,
    useGetJobCategoriesQuery,
    useAddJobCategoryMutation,
    useGetJobPurposesQuery,
    useCreateJobMutation,
    useUpdateJobMutation,
    useAgencyJobListingQuery,
    useGetJobByIdQuery,
    useGetJobsListingMutation,
    useLikeJobMutation,
    useApplyJobMutation,
    useFollowAgentMutation,
    useUnFollowAgentMutation,
    useGetLikedJobsListingQuery,
} = jobsApiSlice;

export const { updateNewJobData, clearJobData, updateSelectedEditJob } =
    jobSlice.actions;

export default jobSlice.reducer;
